import { useState } from 'react'
import { ButtonLink, IconChevronRight, SegmentedControl } from '@n26/nxd'
import {
  Root,
  Trigger,
  Content,
  Item,
  Viewport,
  Link as RadixLink,
} from '@radix-ui/react-navigation-menu'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import {
  Item as AccordionItem,
  Trigger as AccordionTrigger,
  Content as AccordionContent,
  Header as AccordionHeader,
} from '@radix-ui/react-accordion'
import { Text } from '@components/Text'
import type { CardData } from '@cms/data/getHeaderData'
import { Link } from '@components/Link'
import { ResponsiveImage } from '@components/ResponsiveImage'
import { trackGTM } from '@helpers/core/trackGTM'
import { useEventTracker } from '@hooks/useEventTracker'
import { HEADER_SNOWPLOW_NAVIGATION_CLICK } from '@components/Header'
import { useGetLocale } from '@hooks/useGetLocale'

type AccountsNavigationMenuProps = {
  label: string
  businessTabName: string
  personalTabName: string
  businessCards: CardData[]
  personalCards: CardData[]
  cta?: {
    label?: string
    slug?: string
  }
  businessCta?: {
    label?: string
    slug?: string
  }
  isMobile?: boolean
}

type AccountsTabsProps = {
  activeTab: string
  handleTabChange: (value: string) => void
  personalTabName: string
  businessTabName: string
}

type AccountsNavigationMenuViewProps = Omit<
  AccountsNavigationMenuProps,
  'isMobile'
> & {
  activeTab: string
  handleTabChange: (value: string) => void
}

const AccountsTabs = ({
  activeTab,
  handleTabChange,
  personalTabName,
  businessTabName,
}: AccountsTabsProps) => {
  return (
    <SegmentedControl
      value={activeTab}
      onValueChange={handleTabChange}
      size='standard'
    >
      <SegmentedControl.Item value={personalTabName}>
        {personalTabName}
      </SegmentedControl.Item>
      <SegmentedControl.Item value={businessTabName}>
        {businessTabName}
      </SegmentedControl.Item>
    </SegmentedControl>
  )
}

const handleClick = (url: string, label: string) => {
  trackGTM({
    event: 'navigation_click',
    // eslint-disable-next-line camelcase
    destination_url: url,
    // eslint-disable-next-line camelcase
    navigation_text: label,
  })
}

const AccountsDropdownItem = ({ card, label, slug, url }: CardData) => {
  const { track } = useEventTracker()

  return (
    <div className='p-8 hover:bg-neutral-alternate rounded-lg w-full'>
      <RadixLink asChild>
        <Link
          url={slug}
          className='focus:outline-teal flex flex-col items-center'
          onClick={() => {
            track({
              action: HEADER_SNOWPLOW_NAVIGATION_CLICK,
              label: slug,
            })
            handleClick(url, label)
          }}
        >
          <ResponsiveImage
            alt={card}
            originalHeight={80}
            originalWidth={127}
            sources={[{ breakpoint: 0, src: url, width: 127 }]}
          />
          <div className='p-12 font-sans font-medium text-teal text-14 text-center'>
            {label}
          </div>
        </Link>
      </RadixLink>
    </div>
  )
}

const AccountsDropdown = ({
  label,
  businessTabName,
  personalTabName,
  businessCards,
  personalCards,
  businessCta,
  cta,
  activeTab,
  handleTabChange,
}: AccountsNavigationMenuViewProps) => {
  const [value, setValue] = useState('')

  const isActive = value === 'accounts'

  const locale = useGetLocale()
  const slug = activeTab === personalTabName ? cta?.slug : businessCta?.slug
  // This allows backwards compatability with Clownfish as it requires
  // some URLs with the locale prefix and some without
  const CTALink =
    locale && slug ? (slug.includes(locale) ? slug : `/${locale}/${slug}`) : ''

  return (
    <Root value={value} onValueChange={setValue}>
      <Item className='list-none' value='accounts'>
        <Trigger className='outline-teal'>
          <div className='text-neutral text-18 font-regular m-12 cursor-pointer hover:bg-neutral focus:outline-teal focus:border-none'>
            {label}
          </div>
        </Trigger>
        <Content className='bg-neutral shadow-4 w-fit rounded-lg mt-8 p-24 flex flex-col items-center'>
          <div className='w-[240px] mb-16'>
            <AccountsTabs
              activeTab={activeTab}
              handleTabChange={handleTabChange}
              personalTabName={personalTabName}
              businessTabName={businessTabName}
            />
          </div>
          <div className='flex flex-row'>
            {activeTab === personalTabName
              ? personalCards.map(personalCard => (
                  <AccountsDropdownItem
                    card={personalCard.card}
                    cardDesign={personalCard.cardDesign}
                    label={personalCard.label}
                    slug={personalCard.slug}
                    url={personalCard.url}
                    key={`${personalCard.label}-${personalCard.slug}`}
                  />
                ))
              : businessCards.map(businessCard => (
                  <AccountsDropdownItem
                    card={businessCard.card}
                    cardDesign={businessCard.cardDesign}
                    label={businessCard.label}
                    slug={businessCard.slug}
                    url={businessCard.url}
                    key={`${businessCard.label}-${businessCard.slug}`}
                  />
                ))}
          </div>
          <div className='mt-12'>
            <ButtonLink href={CTALink} variant='secondary' size='standard'>
              {activeTab === personalTabName
                ? (cta?.label as string)
                : (businessCta?.label as string)}
            </ButtonLink>
          </div>
        </Content>
        <div className='absolute top-full'>
          <Viewport
            forceMount
            style={{
              display: isActive ? 'block' : 'none',
            }}
          />
        </div>
      </Item>
    </Root>
  )
}

const AccountsAccordion = ({
  label,
  businessTabName,
  personalTabName,
  businessCards,
  personalCards,
  businessCta,
  cta,
  activeTab,
  handleTabChange,
}: AccountsNavigationMenuViewProps) => {
  const { track } = useEventTracker()

  return (
    <AccordionItem value={label}>
      <AccordionHeader className='flex'>
        <AccordionTrigger className='text-neutral font-sans p-12 group flex flex-1 justify-between outline-teal'>
          <Text variant='standard-bold'>{label}</Text>{' '}
          <ChevronRightIcon
            className='text-neutral group-data-[state=open]:rotate-90'
            aria-hidden
          />
        </AccordionTrigger>
      </AccordionHeader>
      <AccordionContent className='text-neutral font-sans px-12 bg-neutral flex flex-col'>
        <div className='py-12'>
          <AccountsTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            personalTabName={personalTabName}
            businessTabName={businessTabName}
          />
        </div>
        {activeTab === personalTabName
          ? personalCards.map(link => (
              <Link
                url={link.slug}
                className='py-12 focus:outline-teal'
                key={`${link.label}-${link.slug}`}
                onClick={() => {
                  track({
                    action: HEADER_SNOWPLOW_NAVIGATION_CLICK,
                    label: link.slug,
                  })
                  handleClick(link.slug, label)
                }}
              >
                {link.label}
              </Link>
            ))
          : businessCards.map(link => (
              <Link
                url={link.slug}
                className='py-12 focus:outline-teal'
                key={`${link.label}-${link.slug}`}
                onClick={() => {
                  track({
                    action: HEADER_SNOWPLOW_NAVIGATION_CLICK,
                    label: link.slug,
                  })
                  handleClick(link.slug, label)
                }}
              >
                {link.label}
              </Link>
            ))}
        <Link
          url={
            activeTab === personalTabName
              ? cta?.slug || ''
              : businessCta?.slug || ''
          }
          className='py-12 focus:outline-teal text-teal font-medium flex flex-row flex-nowrap'
        >
          {activeTab === personalTabName
            ? (cta?.label as string)
            : (businessCta?.label as string)}
          <IconChevronRight color='teal' />
        </Link>
      </AccordionContent>
    </AccordionItem>
  )
}

export const AccountsNavigationMenu = ({
  isMobile,
  label,
  businessTabName,
  personalTabName,
  businessCards,
  personalCards,
  businessCta,
  cta,
}: AccountsNavigationMenuProps) => {
  const [activeTab, setActiveTab] = useState(personalTabName)
  const handleTabChange = (value: string) => setActiveTab(value)

  return isMobile ? (
    <AccountsAccordion
      label={label}
      businessTabName={businessTabName}
      personalTabName={personalTabName}
      businessCards={businessCards}
      personalCards={personalCards}
      businessCta={businessCta}
      cta={cta}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
    />
  ) : (
    <AccountsDropdown
      label={label}
      businessTabName={businessTabName}
      personalTabName={personalTabName}
      businessCards={businessCards}
      personalCards={personalCards}
      businessCta={businessCta}
      cta={cta}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
    />
  )
}
